<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { convertToAttributeSlug } from 'dashboard/helper/commons.js';
import FormInput from 'v3/components/Form/Input.vue';

export default {
  components: {
    FormInput,
  },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'created'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      displayName: '',
      description: '',
      attributeKey: '',
      showModal: false
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'attributes/getUIFlags',
    }),
    isButtonDisabled() {
      return (
        this.v$.displayName.$invalid ||
        this.v$.description.$invalid ||
        this.v$.attributeKey.$invalid ||
        this.uiFlags.isCreating
      );
    },
    keyErrorMessage() {
      if (!this.v$.attributeKey.isKey) {
        return 'A chave não pode conter espaços';
      }
      return 'Chave do campo é obrigatória';
    },
  },
  watch: {
    show(newVal) {
      this.showModal = newVal;
      if (newVal) {
        // Resetar campos quando o modal abrir
        this.displayName = '';
        this.description = '';
        this.attributeKey = '';
        // Resetar validação
        this.v$.$reset();
      }
    }
  },
  validations: {
    displayName: {
      required,
      minLength: minLength(1),
    },
    description: {
      required,
    },
    attributeKey: {
      required,
      isKey(value) {
        return !(value.indexOf(' ') >= 0);
      },
    },
  },
  methods: {
    onDisplayNameChange() {
      this.attributeKey = convertToAttributeSlug(this.displayName);
    },
    handleClose() {
      this.displayName = '';
      this.description = '';
      this.attributeKey = '';
      this.v$.$reset(); // Resetar validação ao fechar
      this.showModal = false;
      this.$emit('close');
    },
    async addAttributes() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      
      try {
        const response = await this.$store.dispatch('attributes/create', {
          attribute_display_name: this.displayName,
          attribute_description: this.description,
          attribute_model: 1, // contact_attribute
          attribute_display_type: 0, // text (sempre texto)
          attribute_key: this.attributeKey,
        });
        
        // Verificar se a resposta tem dados
        if (!response || !response.data) {
          throw new Error('Resposta inválida do servidor');
        }
        
        this.alertMessage = 'Atributo criado com sucesso';
        useAlert(this.alertMessage);
        
        // Recarregar atributos
        await this.$store.dispatch('attributes/get');
        
        // Emitir o novo atributo criado com o ID correto
        const newAttribute = {
          id: response.data.id,
          attributeDisplayName: this.displayName,
          attributeDisplayType: 'text',
          attributeDescription: this.description,
          attributeKey: this.attributeKey
        };
        
        //console.log('Atributo criado com sucesso:', newAttribute); // Debug
        
        // Emitir o evento ANTES de fechar para garantir que o pai receba
        this.$emit('created', newAttribute);
        
        // Depois fechar o modal
        this.handleClose();
      } catch (error) {
        // Melhor tratamento de erro
        let errorMessage = 'Erro ao criar atributo.';
        
        if (error?.message?.includes('already been')) {
          errorMessage = 'Esta chave de atributo já existe. Por favor, use uma chave diferente.';
          // Limpar apenas o campo de chave
          this.attributeKey = '';
          this.v$.attributeKey.$reset();
        } else if (error?.message) {
          errorMessage = error.message;
        }
        
        this.alertMessage = errorMessage;
        useAlert(this.alertMessage);
      }
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <div v-if="showModal" class="fixed inset-0 z-[9999] flex items-center justify-center">
      <!-- Backdrop que cobre toda a tela com blur já aplicado -->
      <transition name="backdrop-fade">
        <div class="absolute inset-0 bg-n-alpha-black2 backdrop-blur-[4px]" @click="handleClose"></div>
      </transition>
      
      <!-- Container do modal com animação separada -->
      <transition name="modal-scale">
        <div class="relative bg-n-alpha-3 rounded-xl w-[37.5rem] max-w-[90%] max-h-[90vh] overflow-auto shadow-md z-[10000]">
          <!-- Botão de fechar - fora do header -->
          <button 
            class="absolute z-10 right-2 top-2 w-8 h-8 flex items-center justify-center text-n-slate-10 hover:text-n-slate-12 hover:bg-n-slate-5/20 rounded transition-colors p-1"
            @click="handleClose"
            type="button"
          >
            <span class="i-lucide-x w-5 h-5 block"></span>
          </button>
          
          <!-- Header do modal -->
          <div class="px-8 pt-9 pb-0">
            <h2 class="text-base font-semibold leading-6 text-slate-800 dark:text-slate-50">
              Adicionar Atributo do Usuário
            </h2>
          </div>

          <!-- Formulário -->
          <form class="pt-4 pb-7 px-8" @submit.prevent="addAttributes">
            <div class="w-full grid gap-4">
              <FormInput
                v-model="displayName"
                name="displayName"
                label="Nome do atributo"
                type="text"
                spacing="compact"
                :has-error="v$.displayName.$error"
                :error-message="
                  v$.displayName.$error
                    ? 'Nome do atributo é obrigatório'
                    : ''
                "
                placeholder="Ex: CPF"
                @input="onDisplayNameChange"
                @blur="v$.displayName.$touch"
              />
              
              <FormInput
                v-model="attributeKey"
                name="attributeKey"
                label="Chave do atributo"
                type="text"
                spacing="compact"
                :has-error="v$.attributeKey.$error"
                :error-message="v$.attributeKey.$error ? keyErrorMessage : ''"
                placeholder="Ex: cpf"
                @blur="v$.attributeKey.$touch"
              />
              
              <div>
                <label class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                  Descrição
                </label>
                <textarea
                  v-model="description"
                  rows="3"
                  class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-800 dark:border-slate-600"
                  :class="{ 'border-red-500': v$.description.$error }"
                  placeholder="Descreva o propósito deste atributo"
                  @blur="v$.description.$touch"
                />
                <span v-if="v$.description.$error" class="text-red-500 text-sm mt-1">
                  Descrição é obrigatória
                </span>
              </div>
              
              <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
                <woot-submit-button
                  :disabled="isButtonDisabled"
                  :button-text="'Adicionar atributo'"
                  :loading="uiFlags.isCreating"
                />
                <button class="button clear" @click.prevent="handleClose">
                  Cancelar
                </button>
              </div>
            </div>
          </form>
        </div>
      </transition>
    </div>
  </Teleport>
</template>

<style lang="scss">
// Animação do backdrop - sem delay
.backdrop-fade-enter-active,
.backdrop-fade-leave-active {
  transition: opacity 0.15s ease-out;
}

.backdrop-fade-enter-from,
.backdrop-fade-leave-to {
  opacity: 0;
}

// Animação do modal - scale
.modal-scale-enter-active,
.modal-scale-leave-active {
  transition: all 0.2s ease-out;
}

.modal-scale-enter-from,
.modal-scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>